import { PureComponent, ReactNode, useEffect } from 'react';
import Select, {
    components,
    ControlProps,
    ValueContainerProps,
    OptionProps,
    MenuProps,
    StylesConfig,
    ValueType,
    MenuListComponentProps,
    ContainerProps,
} from 'react-select';
import { GroupHeadingProps } from 'react-select/src/components/Group';
import debounce from 'lodash/debounce';
import { CSSObject } from '@emotion/serialize';

import { PAD_V2_CONTAINER } from '../PadContainer';
import onContainerClick from '../../../../../pages/prescriptionPad/onClickContainer';
import { escTextIcon } from '../../../../common/Icons';
import { handleKeyDownEvent } from '../handleKeyDownEvent';
import { LOCAL_ID_PREFIX } from '../../../../../constants';
import { CUSTOM } from '../../../trackSource';
import { REFERRAL_AS_EL_ID } from '../../../leftPanel/DoctorReferralSearchInput';
import { DENTAL_ENTITY_TYPE } from '../../../../../store/types/prescription';
import { connect } from 'react-redux';
import { GlobalState } from '../../../../../store/reducers';
import { getGenieSetting } from '../../../../../store/selectors/session';
import { formattedNumber } from '../../../../../pages/utils';
import { GTreatmentService } from '../../../../../store/db';
import { Popover } from 'antd';

type OPTION_SOURCE = 'GENIE' | 'SEARCH' | 'RELATED' | 'CUSTOM';

interface OptionItem {
    id: string;
    name: string;
}

interface OptionItemObject {
    autosuggestHeading?: string;
    type?: DENTAL_ENTITY_TYPE;
    is_panel?: boolean;
}

// export type OptionItem = Item;

export interface CustomDataForAS {
    [k: string]: {
        optionIconText: JSX.Element;
        optionIconBgClass: string;
    };
}

export type AllOptions<T extends OptionItem> = ({
    label: LABEL_TYPE;
    options: T[];
} & OptionItemObject)[];

interface Props<T extends OptionItem> {
    onSelect: (sym: T) => void;

    elements: ReadonlyArray<T>;
    placeholder: string;
    optionIconText: JSX.Element;
    templateIconText?: JSX.Element;
    optionIconBgClass: string;
    menuListClassName: string;
    optionWrapperClass: string;
    inputId?: string;
    elId?: string;
    autoFocus?: boolean;
    targetContainer?: string;
    getOptions: (
        setOptions: (options: AllOptions<T>, inputValue?: string) => void,
        input?: string,
    ) => void;
    autosuggestHeading?: string;
    disabled?: boolean;
    hideKeyboardShortCutsInfo?: boolean;
    customDataForAS?: CustomDataForAS;
    disableAutoScrollOnSelect?: boolean;
    style?: { [key: string]: string };
}

const DropdownIndicator = null;

export const SYMPTOMS = 'Symptoms';
export const DIAGNOSIS = 'Diagnosis';

const Option = (props: OptionProps<OptionItem, true>) => {
    const { data, selectProps } = props;
    const {
        focussedItemId,
        setFocussedId,
        customDataForAS,
        hideKeyboardShortCutsInfo,
    } = selectProps;
    const {
        optionIconText,
        templateIconText,
        optionIconBgClass,
        optionWrapperClass,
    }: Props<OptionItem> = selectProps.config;

    const { id, name, price, services } = data;
    const servicesIncludedInPackage = services as GTreatmentService[];

    useEffect(() => {
        if (props.isFocused) {
            setFocussedId(data.id);
        }
    }, [props.isFocused]);

    const isFocussed = focussedItemId === data.id;

    const isAddNewDoctor =
        id?.startsWith(LOCAL_ID_PREFIX) &&
        data?.track?.source === CUSTOM &&
        props?.selectProps?.id === REFERRAL_AS_EL_ID;

    const clinicAddress = [
        data?.doc?.clinic?.landmark,
        data?.doc?.clinic?.city,
        data?.doc?.clinic?.state,
        data?.doc?.clinic?.pincode,
    ]
        .filter(Boolean)
        .join(' ');
    const dataForType = customDataForAS?.[data?.type] || {};

    return (
        <components.Option {...props} isFocused={isFocussed}>
            <div
                key={id}
                id={id}
                className={`flex items-center justify-between space-x-24 cursor-pointer px-8 ${
                    isFocussed ? 'focus-state-dropdown-item' : `${optionWrapperClass}`
                }`}
            >
                {isAddNewDoctor ? (
                    <div className="flex items-center py-10 space-x-8">
                        <div className="flex items-center w-16 h-16 justify-center m-4">
                            <svg
                                width="14"
                                height="14"
                                viewBox="0 0 14 14"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M13.5 7C13.5 7.4375 13.1562 7.75 12.75 7.75L7.75 7.75L7.75 12.75C7.75 13.1875 7.40625 13.5313 7 13.5313C6.5625 13.5313 6.25 13.1875 6.25 12.75L6.25 7.75H1.25C0.8125 7.75 0.5 7.4375 0.5 7.03125C0.5 6.59375 0.8125 6.25 1.25 6.25H6.25V1.25C6.25 0.84375 6.5625 0.53125 7 0.53125C7.40625 0.53125 7.75 0.84375 7.75 1.25V6.25L12.75 6.25C13.1562 6.25 13.5 6.59375 13.5 7Z"
                                    fill="#004BA8"
                                />
                            </svg>
                        </div>
                        <span className="text-14 text-darwin-primary-500">
                            Add doctor <span className="font-700">" {data.name} "</span>
                        </span>
                    </div>
                ) : (
                    <div className="flex items-start py-4 space-x-8 w-full">
                        {data?.doc ? (
                            <>
                                <div className="whitespace-nowrap flex items-center space-x-8">
                                    {data?.doc?.type === 'speciality' ? (
                                        <div
                                            className={
                                                'w-28 h-28 flex justify-center items-center text-12 font-700 bg-darwin-accent-medical-history-orange-200 rounded-full'
                                            }
                                        >
                                            <p>{'S'}</p>
                                        </div>
                                    ) : (
                                        <img
                                            src={`https://a.eka.care/doctor-avatar/${data?.id}`}
                                            className="h-28 w-28 object-cover rounded-full overflow-hidden"
                                            alt=""
                                        />
                                    )}
                                    <div>
                                        <span className="flex items-center space-x-4">
                                            <p className="text-neutral-1 text-14">
                                                {data?.doc?.type === 'speciality'
                                                    ? data?.doc?.s_name
                                                    : name}
                                            </p>
                                            <div>
                                                {data?.referred_doctor_oid && (
                                                    <svg
                                                        className="w-16 h-16"
                                                        viewBox="0 0 105 117"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            d="M104.436 33.768C104.144 38.8643 102.1 43.6607 98.4497 47.408L63.4061 83.3816C62.53 84.2809 61.5079 85.1803 60.3398 85.9297C52.3089 91.4757 41.3578 90.4264 34.4951 83.3816C30.2607 79.0348 28.0704 72.8893 28.6545 66.5939C28.8005 62.8467 31.8668 59.9988 35.5172 59.9988C39.3136 59.9988 42.3799 63.1464 42.3799 67.0436C42.3799 69.142 41.5038 70.9407 40.1897 72.2897C40.6277 73.4889 41.3578 74.688 42.3799 75.5873C44.1321 77.386 46.6143 78.4352 49.0966 78.4352C50.7028 78.4352 52.1629 78.1354 53.4771 77.386C54.3531 76.9363 55.0832 76.3368 55.8133 75.5873L73.0431 57.9003L65.8883 50.5557L55.6673 40.0634C53.9151 38.2647 51.4328 37.2155 48.9506 37.2155C46.4683 37.2155 43.9861 38.2647 42.2339 40.0634C41.0658 41.2625 40.1897 42.7614 39.7516 44.2603C41.3578 45.6093 42.5259 47.5579 42.5259 49.8062C42.5259 53.7034 39.4596 56.8511 35.6632 56.8511C32.1589 56.8511 29.0925 54.0032 28.8005 50.4058C27.6324 43.6607 29.6766 36.9157 34.4951 31.9693C38.2915 28.0722 43.402 25.8238 48.9506 25.8238C54.3531 25.8238 59.6097 28.0722 63.4061 31.9693L73.6271 42.4616L80.7819 49.8062L90.7109 39.6137C92.6091 37.6651 93.4852 35.2669 93.4852 32.5689C93.4852 30.1706 92.6091 27.9223 91.0029 26.2735C86.7685 21.477 81.658 17.7298 75.9634 15.0318C69.9768 12.3337 63.6981 10.8348 57.1274 10.8348C44.8622 10.8348 33.473 15.7812 24.7121 24.6247C16.0972 33.4682 11.2787 45.3095 11.2787 57.9003C11.2787 70.4911 16.0972 82.1825 24.7121 91.1759C33.327 100.019 44.8622 104.966 57.1274 104.966C63.6981 104.966 69.9768 103.617 75.9634 100.769C81.658 98.0708 86.7685 94.3236 91.0029 89.5271C94.3613 85.6299 94.2152 79.7842 90.7109 76.037L83.4101 68.5425L91.1489 60.5983L98.4497 68.0928C106.188 76.037 106.481 88.6277 99.1798 97.0216C93.9232 103.017 87.6446 107.664 80.6359 110.961C73.3351 114.409 65.4503 116.058 57.2734 116.058C49.6807 116.058 42.2339 114.559 35.2252 111.561C28.5085 108.563 22.3758 104.366 17.2653 99.12C12.0088 93.724 7.92036 87.5785 5.14608 80.6836C2.22578 73.4889 0.765625 65.8445 0.765625 58.0502C0.765625 50.2559 2.22578 42.6115 5.14608 35.4168C8.06638 28.5219 12.1548 22.2265 17.2653 16.9803C22.5219 11.5843 28.5085 7.38737 35.2252 4.53946C42.2339 1.54166 49.6807 0.042762 57.2734 0.042762C65.4503 0.042762 73.1891 1.84143 80.6359 5.13901C87.7906 8.43659 93.9232 13.0832 99.1798 19.0788C101.37 21.6269 103.122 24.7746 103.852 28.0722C104.29 29.4212 104.436 31.6695 104.436 33.768Z"
                                                            fill="#7265E3"
                                                        ></path>
                                                    </svg>
                                                )}
                                            </div>
                                        </span>
                                        <div className="uppercase text-11 text-darwin-neutral-600">
                                            {[
                                                data?.doc?.type !== 'speciality' &&
                                                    data?.doc.s_name,
                                                data?.doc?.phone?.value &&
                                                    `${
                                                        data?.doc?.phone?.code
                                                            ? `${data?.doc?.phone?.code} `
                                                            : ''
                                                    }${data?.doc?.phone?.value}`,
                                            ]
                                                .filter(Boolean)
                                                .join(' | ')}
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div
                                    className={
                                        customDataForAS
                                            ? dataForType?.optionIconBgClass || optionIconBgClass
                                            : optionIconBgClass
                                    }
                                >
                                    <p>
                                        {data?.isTemplate
                                            ? templateIconText
                                            : customDataForAS
                                            ? dataForType.optionIconText || optionIconText
                                            : optionIconText}
                                    </p>
                                </div>
                                <div className="flex-1 flex items-start justify-between space-x-8 pt-3">
                                    <div>
                                        <p className="text-neutral-1 text-14">{name}</p>
                                        {Array.isArray(servicesIncludedInPackage) &&
                                            servicesIncludedInPackage.length > 0 && (
                                                <div className="flex items-center text-darwin-neutrals-600 text-12 space-x-8">
                                                    <p className="truncate-overflow-1">
                                                        Includes{' '}
                                                        {servicesIncludedInPackage
                                                            .map((i: GTreatmentService) => i.name)
                                                            .join(', ')}
                                                    </p>
                                                    <Popover
                                                        placement="right"
                                                        title={
                                                            <p className="font-700 select-none">
                                                                Services included in this package (
                                                                {servicesIncludedInPackage.length})
                                                            </p>
                                                        }
                                                        content={
                                                            <div className="space-y-4 select-none overflow-auto max-h-328 max-w-400">
                                                                {servicesIncludedInPackage.map(
                                                                    (service, index) => (
                                                                        <p className="flex items-start space-x-8">
                                                                            <p>{index + 1}.</p>
                                                                            <div className="flex-1 flex items-start justify-between">
                                                                                <p className="flex-1">
                                                                                    {service.name}
                                                                                </p>
                                                                                <p className="text-right w-80">
                                                                                    ₹{service.price}
                                                                                </p>
                                                                            </div>
                                                                        </p>
                                                                    ),
                                                                )}
                                                            </div>
                                                        }
                                                        trigger="hover"
                                                        overlayClassName="max-w-328"
                                                    >
                                                        <p>
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                className="w-16 h-16 fill-darwin-primary-500"
                                                                viewBox="0 0 12 13"
                                                            >
                                                                <path d="M6 0.5C2.67188 0.5 0 3.19531 0 6.5C0 9.82812 2.67188 12.5 6 12.5C9.30469 12.5 12 9.82812 12 6.5C12 3.19531 9.30469 0.5 6 0.5ZM6 11.375C3.30469 11.375 1.125 9.19531 1.125 6.5C1.125 3.82812 3.30469 1.625 6 1.625C8.67188 1.625 10.875 3.82812 10.875 6.5C10.875 9.19531 8.67188 11.375 6 11.375ZM6.9375 8.375H6.5625V6.3125C6.5625 6.00781 6.30469 5.75 6 5.75H5.25C4.92188 5.75 4.6875 6.00781 4.6875 6.3125C4.6875 6.64062 4.92188 6.875 5.25 6.875H5.4375V8.375H5.0625C4.73438 8.375 4.5 8.63281 4.5 8.9375C4.5 9.26562 4.73438 9.5 5.0625 9.5H6.9375C7.24219 9.5 7.5 9.26562 7.5 8.9375C7.5 8.63281 7.24219 8.375 6.9375 8.375ZM6 5C6.39844 5 6.75 4.67188 6.75 4.25C6.75 3.85156 6.39844 3.5 6 3.5C5.57812 3.5 5.25 3.85156 5.25 4.25C5.25 4.67188 5.57812 5 6 5Z" />
                                                            </svg>
                                                        </p>
                                                    </Popover>
                                                </div>
                                            )}
                                    </div>
                                    {typeof price === 'number' && (
                                        <p className="text-neutral-1 text-14">
                                            ₹{formattedNumber(price)}
                                        </p>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                )}

                {clinicAddress?.trim() ? (
                    <div className="h-full flex items-start">
                        <div className="uppercase text-11 text-darwin-neutral-600">
                            {clinicAddress}
                        </div>
                    </div>
                ) : !hideKeyboardShortCutsInfo ? (
                    <div className="flex items-center space-x-12 movement-controls">
                        <div className="flex items-center space-x-4">
                            <svg
                                className="fill-neutral-5 w-14 h-14"
                                role="img"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 448 512"
                            >
                                <path d="M384 32H64C28.66 32 0 60.66 0 96v320c0 35.34 28.66 64 64 64h320c35.34 0 64-28.66 64-64V96C448 60.66 419.3 32 384 32zM350.6 254.6c-12.5 12.5-32.75 12.5-45.25 0L256 205.3V384c0 17.69-14.33 32-32 32s-32-14.31-32-32V205.3L142.6 254.6c-12.5 12.5-32.75 12.5-45.25 0s-12.5-32.75 0-45.25l103.1-103.1C209.3 97.4 219.1 96 224 96c4.881 0 14.65 1.391 22.65 9.398l103.1 103.1C363.1 221.9 363.1 242.1 350.6 254.6z"></path>
                            </svg>
                            <p className="text-neutral-1 text-12">Up</p>
                        </div>
                        <div className="flex items-center space-x-4">
                            <svg
                                className="w-14 h-14 fill-neutral-5"
                                role="img"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 448 512"
                            >
                                <path d="M384 32H64C28.66 32 0 60.66 0 96v320c0 35.34 28.66 64 64 64h320c35.34 0 64-28.66 64-64V96C448 60.66 419.3 32 384 32zM350.6 302.6l-103.1 103.1C238.7 414.6 228.9 416 224 416c-4.881 0-14.65-1.391-22.65-9.398L97.38 302.6c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0L192 306.8V128c0-17.69 14.33-32 32-32s32 14.31 32 32v178.8l49.38-49.38c12.5-12.5 32.75-12.5 45.25 0S363.1 290.1 350.6 302.6z"></path>
                            </svg>
                            <p className="text-neutral-1 text-12">Down</p>
                        </div>
                        <div className="flex items-center space-x-4">
                            <svg
                                width="14"
                                height="14"
                                viewBox="0 0 14 14"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <rect
                                    x="0.5"
                                    y="0.5"
                                    width="13"
                                    height="13"
                                    rx="1.5"
                                    fill="#858C94"
                                    stroke="#858C94"
                                ></rect>
                                <path
                                    d="M2.90527 7.85927C8.8874 7.85927 10.803 7.85927 10.803 7.85927V3.57617"
                                    stroke="#E5EDF6"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                ></path>
                                <path
                                    d="M4.50024 5.5L1.77344 8.40059L4.50024 11"
                                    stroke="#E5EDF6"
                                    strokeWidth="1.5"
                                ></path>
                            </svg>
                            <p className="text-neutral-1 text-12">Select</p>
                        </div>
                    </div>
                ) : (
                    <></>
                )}
            </div>
        </components.Option>
    );
};

// TODO
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const Input = (props) => {
    const { innerRef, selectProps, onChange, onFocus, value } = props;
    const { placeholder, setMenuOnOff, customProps } = selectProps;
    const { onBlur } = customProps;

    const { inputId }: Props<OptionItem> = selectProps.config;

    const onBBlur = (): void => {
        setMenuOnOff(false);
        onBlur();
    };

    const onFFocus = (): void => {
        setMenuOnOff(true);
        onFocus();
    };

    return (
        <div className="flex items-center justify-between w-full bg-transparent">
            <div className="flex items-center space-x-12 w-full">
                <svg
                    className="w-16 h-16 fill-darwin-neutral-400"
                    role="img"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                >
                    <path d="M504.1 471l-134-134C399.1 301.5 415.1 256.8 415.1 208c0-114.9-93.13-208-208-208S-.0002 93.13-.0002 208S93.12 416 207.1 416c48.79 0 93.55-16.91 129-45.04l134 134C475.7 509.7 481.9 512 488 512s12.28-2.344 16.97-7.031C514.3 495.6 514.3 480.4 504.1 471zM48 208c0-88.22 71.78-160 160-160s160 71.78 160 160s-71.78 160-160 160S48 296.2 48 208z"></path>
                </svg>
                <input
                    id={inputId}
                    ref={innerRef}
                    type="text"
                    className="w-full text-14 placeholder-darwin-neutral-400 text-neutral-black py-10 bg-transparent"
                    placeholder={placeholder}
                    value={value}
                    onChange={onChange}
                    onFocus={onFFocus}
                    onClick={onFFocus}
                    onBlur={onBBlur}
                    autoComplete="off"
                />
            </div>
        </div>
    );
};

const ValueContainer = (props: ValueContainerProps<OptionItem, true>) => {
    const { children } = props;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const child = children[children.length - 1];

    return child;
};

const Control = (props: ControlProps<OptionItem, true>) => {
    const { children } = props;
    return <components.Control {...props}>{children}</components.Control>;
};

const LoadingIndicator = null;

const Menu = (props: MenuProps<OptionItem, true>) => {
    const { getValue, options } = props;

    const selectedValues = getValue();

    // const { inputValue } = props.selectProps;

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const allOptions = options.map((option) => option.options).flat();
    if (
        // (!inputValue && selectedValues.length === 0) ||
        // TODO check if this can be improved
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        (allOptions?.length || 0) === 0 &&
        selectedValues.length === 0
    ) {
        return null;
    }

    const { children, ...otherProps } = props;

    return (
        <components.Menu {...otherProps}>
            <>{children}</>
        </components.Menu>
    );
};

const GroupHeading = (
    props: GroupHeadingProps<{ label: LABEL_TYPE; options: OptionItem[] }, true> & {
        data?: OptionItemObject;
    },
) => {
    const { children, selectProps, data } = props;
    const { options, autosuggestHeading, hideKeyboardShortCutsInfo } = selectProps;
    const escIcon = escTextIcon('h-20 w-20');

    if (
        (!options ||
            (children !== AS_OPTIONS_FREQUENCTLY &&
                children !== AS_OPTIONS_SUGGESTED &&
                children !== AS_OPTIONS_RECENTLY)) &&
        !data?.autosuggestHeading
    ) {
        return null;
    }

    return (
        <div className="flex items-center justify-between px-8 pb-4">
            {children === AS_OPTIONS_FREQUENCTLY ? (
                <div className="flex items-center space-x-4">
                    <div className="bg-darwin-accent-bright-blue-800 px-6 py-4 caption-1 bold font-700 text-white rounded-4">
                        {data?.autosuggestHeading || 'frequently'}
                    </div>
                    <p className="text-neutral-6 text-12 font-700">
                        {autosuggestHeading || 'seen by you'}
                    </p>
                </div>
            ) : children === AS_OPTIONS_SUGGESTED ? (
                <div className="flex items-center space-x-4">
                    <div className="bg-darwin-accent-sulphur-green-500 px-6 py-4 caption-1 bold text-white rounded-4">
                        associated
                    </div>
                    <p className="text-neutral-6 text-12 font-700">
                        {/* @todo add customization  */}
                        {/* to Symptoms / Diagnosis Entered */}
                    </p>
                </div>
            ) : children === AS_OPTIONS_RECENTLY ? (
                <div className="flex items-center space-x-4">
                    <div className="bg-darwin-accent-bright-blue-800 px-6 py-4 caption-1 bold font-700 text-white rounded-4">
                        Recently
                    </div>
                    <p className="text-neutral-6 text-12 font-700">
                        {autosuggestHeading || 'seen by you'}
                    </p>
                </div>
            ) : data?.autosuggestHeading ? (
                <div className="flex items-center space-x-4">
                    <div className="bg-darwin-accent-bright-blue-800 px-6 py-4 caption-1 bold font-700 text-white rounded-4">
                        {data?.autosuggestHeading}
                    </div>
                </div>
            ) : (
                <div />
            )}
            {!hideKeyboardShortCutsInfo &&
                options &&
                options.length > 0 &&
                options[options.length - 1].label === children && (
                    <div className="flex items-center space-x-4">
                        {escIcon}
                        <p className="text-neutral-1 body-3">Close</p>
                    </div>
                )}
        </div>
    );
};

const MenuList = (props: MenuListComponentProps<OptionItem, true>) => {
    const { selectProps } = props;
    const { menuListClassName }: Props<OptionItem> = selectProps.config;
    return (
        <components.MenuList
            {...props}
            className={`absolute flex z-10 w-full px-8 pt-12 overflow-auto bg-white top-40 shadow-tight-3 rounded-8 space-y-4 ${menuListClassName}`}
        >
            {props.children}
        </components.MenuList>
    );
};

const SelectContainer = (props: ContainerProps<OptionItem, true>) => (
    <components.SelectContainer {...props}>
        <div className="relative">{props.children}</div>
    </components.SelectContainer>
);

const NoOptionsMessage = () => null;
const LoadingMessage = () => null;

const customComponents = {
    DropdownIndicator,
    ValueContainer,
    Input,
    Control,
    Menu,
    MenuList,
    Option,
    LoadingIndicator,
    GroupHeading,
    NoOptionsMessage,
    LoadingMessage,
    SelectContainer,
};

const customStyles: StylesConfig<OptionItem, true> = {
    option: (base) => {
        return {
            ...base,
            padding: 0,
        } as CSSObject;
    },
    menuList: () => {
        return {
            overflow: 'auto',
            maxHeight: 'calc(100vh - 180px)',
        } as CSSObject;
    },
    group: (base) => {
        return {
            ...base,
            paddingTop: 0,
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
        } as CSSObject;
    },
    container: () => {
        return { width: '100%' } as CSSObject;
    },
    menu: () => {
        return {} as CSSObject;
    },
    control: () => {
        return {} as CSSObject;
    },
};

export const AS_OPTIONS_FREQUENCTLY = 'FREQUENTLY';
export const AS_OPTIONS_SUGGESTED = 'SUGGESTED';
export const AS_OPTIONS_SEARCH = 'SEARCH';
export const AS_OPTIONS_RECENTLY = 'RECENTLY';

export type LABEL_TYPE =
    | typeof AS_OPTIONS_FREQUENCTLY
    | typeof AS_OPTIONS_SUGGESTED
    | typeof AS_OPTIONS_SEARCH
    | typeof AS_OPTIONS_RECENTLY;

type State<T extends OptionItem> = {
    isMenuOpen: boolean;
    selectedValues: OptionItem[];
    inputValue: string;

    options: AllOptions<T>;

    focussedItemId: string | undefined;
};

class PadV2AutoSuggest<T extends OptionItem> extends PureComponent<Props<T>, State<T>> {
    fetchDebounce: (input: string) => void;
    ref: Select<OptionItem, true> | null = null;

    state: State<T> = {
        isMenuOpen: false,
        selectedValues: [],
        inputValue: '',

        options: [],

        focussedItemId: undefined,
    };

    getOptionValue = (e: OptionItem): string => e.name;

    constructor(props: Props<T>) {
        super(props);

        this.fetchDebounce = debounce(this.loadOptions, 300);
    }

    setOptionsWhenInputValueIsEmpty = (timeout: number): void => {
        setTimeout(() => {
            const { getOptions } = this.props;
            getOptions(this.setOptions, this.state.inputValue);
        }, timeout);
    };

    componentDidUpdate = (prevProps: Props<T>): void => {
        if (prevProps.elements !== this.props.elements) {
            this.setOptionsWhenInputValueIsEmpty(0);
        }
    };

    onSelect = (options: ValueType<OptionItem, true>): void => {
        this.setState({
            selectedValues: [],
        });

        if (!options || options.length !== 1) {
            return;
        }

        const option = options[0];

        const { onSelect } = this.props;

        onSelect(option as T);

        // track('PAD_AS_SELECT', {
        //     ...option._ui,
        //     label: labelSelected,
        //     index: indexSelected.toString(),
        // });

        this.setState({
            selectedValues: [],
            isMenuOpen: false,
            inputValue: '',
            focussedItemId: undefined,
        });

        this.adjustScroll();

        this.ref?.blur();
    };

    onInputChange = (newValue: string): void => {
        this.setState({
            inputValue: newValue,
            isMenuOpen: true,
        });

        if (!newValue) {
            this.setOptionsWhenInputValueIsEmpty(0);
        } else {
            this.fetchDebounce(newValue);
        }
    };

    onInputFocus = (): void => {
        const { inputValue } = this.state;

        if (!inputValue) this.setOptionsWhenInputValueIsEmpty(0);

        this.adjustScroll();
    };

    setOptions = (options: AllOptions<T>, inputValue?: string): void => {
        if (this.state.inputValue !== inputValue) return;

        const focussedItemId = options.find((list) => list.options.length > 0)?.options[0].id;

        this.setState({
            options: options,
            focussedItemId: focussedItemId,
        });
    };

    loadOptions = (inputValue: string): void => {
        if (!inputValue || inputValue.length == 0) {
            return;
        }

        const { getOptions } = this.props;

        getOptions(this.setOptions, inputValue);
    };

    adjustScroll = (): void => {
        const { elId } = this.props;

        if (!elId || this.props.disableAutoScrollOnSelect) {
            return;
        }

        const el = document.getElementById(elId) as HTMLDivElement;
        const con = document.getElementById(
            this.props.targetContainer || PAD_V2_CONTAINER,
        ) as HTMLDivElement;

        if (el && con) {
            const diff = 80 - (con.offsetHeight - (294 + el.getBoundingClientRect().y));
            diff > 0 &&
                con.scrollBy({
                    top: diff,
                    behavior: 'smooth',
                });
        }
    };

    filterOption = (): boolean => true;

    setMenuOnOff = (value: boolean): void => {
        this.setState({ isMenuOpen: value });
    };

    onBlur = (): void => {
        this.setState({
            selectedValues: [],
            isMenuOpen: false,
        });
        onContainerClick();
    };

    scrollIntoView = (id: string): void => {
        try {
            const myContainer = document.getElementById(id) as HTMLDivElement;
            if (myContainer) {
                myContainer.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'nearest',
                });
            }
        } catch (e) {
            console.error(e);
        }
    };

    setFocus = (newId: string | undefined): void => {
        this.setState({
            focussedItemId: newId,
        });
    };

    onKeyDown = (event: React.KeyboardEvent<HTMLElement>): void => {
        if ((event.key === ' ' || event.keyCode === 32) && !this.state.inputValue) {
            event.preventDefault();
            return;
        }

        if (event.key === 'Escape') {
            this.ref?.blur();
            this.setState({
                isMenuOpen: false,
            });
            event.stopPropagation();
            event.preventDefault();
            return;
        }

        const { focussedItemId, options, isMenuOpen } = this.state;

        handleKeyDownEvent<OptionItem>({
            event,
            focussedItemId,
            leftList: options.length > 0 ? options[0].options : [],
            rightList: options.length > 1 ? options[1].options : [],
            setFocus: this.setFocus,
            isMenuOpen,
            // TODO
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            onSelect: this.onSelect,
            scrollIntoView: this.scrollIntoView,
        });
    };

    render(): ReactNode {
        const {
            elId,
            placeholder,
            autoFocus = false,
            autosuggestHeading,
            disabled,
            hideKeyboardShortCutsInfo,
            customDataForAS,
            style,
        } = this.props;
        const { inputValue, isMenuOpen, selectedValues, options, focussedItemId } = this.state;

        return (
            <div
                className="mt-8 w-full rounded-8 border border-neutral-7 px-12 flex items-center row-input-container relative bg-white"
                style={style}
            >
                <Select
                    ref={(ref) => (this.ref = ref)}
                    id={elId}
                    value={selectedValues}
                    isClearable={false}
                    placeholder={placeholder}
                    components={customComponents}
                    styles={customStyles}
                    filterOption={this.filterOption}
                    getOptionValue={this.getOptionValue}
                    options={options}
                    onInputChange={this.onInputChange}
                    inputValue={inputValue}
                    onChange={this.onSelect}
                    onFocus={this.onInputFocus}
                    onKeyDown={this.onKeyDown}
                    focussedItemId={focussedItemId}
                    setFocussedId={this.setFocus}
                    isMulti
                    menuIsOpen={isMenuOpen}
                    customProps={{ onBlur: this.onBlur }}
                    tabSelectsValue={false}
                    setMenuOnOff={this.setMenuOnOff}
                    menuPlacement="auto"
                    config={this.props}
                    autoFocus={autoFocus}
                    autosuggestHeading={autosuggestHeading}
                    hideKeyboardShortCutsInfo={hideKeyboardShortCutsInfo}
                    customDataForAS={customDataForAS}
                />
            </div>
        );
    }
}

export default connect((state: GlobalState) => ({
    disableAutoScrollOnSelect: getGenieSetting(state, 'disableAutoScrollOnSelect') as boolean,
}))(PadV2AutoSuggest);
